<template>
  <div class="-numeric-range">
    <input
      :class="{
        '-range-min': isValueMin,
        '-range-max': isValueMax,
      }"
      type="range"
      ref="input"
      :min="min"
      :max="max"
      :step="step"
      :value="value"
      :disabled="disabled"
      @focusin="isFocused = true"
      @focusout="isFocused = false"
      @input="updateValue($event.target.value)"
      @keydown.right.left="disableCloseToPoint = true"
      @keyup.right.left="disableCloseToPoint = false"
      @change="onChange"
      @mousemove="isFocused && updateRange($event.target.value, $event)"
    />
    <span class="-fake-range" ref="span"></span>
  </div>
</template>

<script>
export default {
  props: {
    value: {
      type: [String, Number],
      default: 0,
    },

    max: {
      type: Number,
      default: 100,
    },

    min: {
      type: Number,
      default: 0,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    step: {
      type: Number,
      default: 1,
    },

    trackColor: {
      type: String,
      default: '#c5cace',
    },

    trackActiveColor: {
      type: String,
      default: '#00aaf5',
    },

    format: {
      type: Function,
      default: (value) => parseFloat(value),
    },

    pointsSnap: {
      type: Array,
      default: () => [],
    },

    snapTolerance: {
      type: Number,
      default: 20, // percentage
    },
  },

  data() {
    return {
      isFocused: false,
      movementX: 0,
      disableCloseToPoint: false,
    };
  },

  computed: {
    pointsSnapToNumber() {
      return _.map(this.pointsSnap, (value) => {
        return _.isFunction(this.format) ? this.format(value) : value;
      });
    },

    isValueMin() {
      return this.getFormattedValue(this.value) === this.min;
    },

    isValueMax() {
      return this.getFormattedValue(this.value) === this.max;
    },
  },

  mounted() {
    this.updateRange(this.value);
  },

  watch: {
    value(newValue) {
      this.updateRange(newValue);
    },
    max(newValue) {
      if (newValue > 0 && this.value === 0) {
        this.updateRange(0);
      }
    },
    isFocused(newValue) {
      if (newValue) {
        this.$emit('focus');
      }
    },
  },

  methods: {
    getFormattedValue(value) {
      return _.isFunction(this.format) ? this.format(value) : value;
    },

    calcTolerance(distance) {
      return distance * (this.snapTolerance / 100);
    },

    isBetweenBackward(point, value, nextPoint, prevPoint) {
      const distance = Math.abs((prevPoint ? prevPoint : nextPoint) - point);
      return value >= point && point + this.calcTolerance(distance) >= value;
    },

    isBetweenForward(point, value, nextPoint, prevPoint) {
      const distance = Math.abs((nextPoint ? nextPoint : prevPoint) - point);
      return value <= point && value + this.calcTolerance(distance) >= point;
    },

    closeToPoint(value) {
      // if we don't know direction, don't try to snap
      if (
        !this.movementX ||
        _.isEmpty(this.pointsSnap) ||
        this.disableCloseToPoint
      ) {
        return value;
      }

      const forward = this.movementX > 0;

      const point = _.find(this.pointsSnapToNumber, (point, index) => {
        const nextPoint = this.pointsSnapToNumber[index + 1];
        const prevPoint = this.pointsSnapToNumber[index - 1];

        return forward
          ? this.isBetweenForward(point, value, nextPoint, prevPoint)
          : this.isBetweenBackward(point, value, nextPoint, prevPoint);
      });

      return _.isDefined(point) ? point : value;
    },

    updateValue(value) {
      const formatValue = _.flow(this.getFormattedValue, this.closeToPoint);

      const newValue = formatValue(value);
      this.$refs.input.value = newValue;
      this.$emit('input', newValue);
    },

    updateRange(value, e) {
      if (this.max === 0) {
        return;
      }
      const formatValue = this.getFormattedValue(value);
      const newRange = ((formatValue - this.min) / (this.max - this.min)) * 100;

      // if it's zero, we don't want to update
      if (e && e.movementX) {
        this.movementX = e.movementX;
      }

      this.$refs.span.style.backgroundImage = `linear-gradient(
            to right,
            ${this.trackActiveColor} ${newRange}%,
            ${this.trackColor} ${newRange}%
          )`.normalizeSpace();
    },

    onChange(e) {
      if (e && e.target) {
        e.target.blur && e.target.blur();
        const formatValue = this.getFormattedValue(e.target.value);
        this.$emit('change', formatValue);
      }
    },
  },
};
</script>
